import React, { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from '../../bravosIcons';
import './TrainerDialog.css';

export type TrainerDialogSize = 'sm' | 'md' | 'lg';

export type TrainerDialogProps = {
  open: boolean;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  size?: TrainerDialogSize;
  role?: 'dialog' | 'alertdialog';
  titleId?: string;
  descriptionId?: string;
  showClose?: boolean;
  closeDisabled?: boolean;
  closeLabel?: string;
  onClose?: () => void;
  dismissOnScrim?: boolean;
  portal?: boolean;
  className?: string;
  panelClassName?: string;
  scrimClassName?: string;
  zIndex?: number;
};

/** Thin center-dialog shell: scrim + panel + title/desc/close/footer slots. */
export function TrainerDialog({
  open,
  title,
  description,
  children,
  footer,
  size = 'md',
  role = 'dialog',
  titleId: titleIdProp,
  descriptionId: descriptionIdProp,
  showClose = true,
  closeDisabled = false,
  closeLabel = 'Close',
  onClose,
  dismissOnScrim = true,
  portal = true,
  className,
  panelClassName,
  scrimClassName,
  zIndex,
}: TrainerDialogProps) {
  const reactId = useId();
  const titleId = titleIdProp ?? `tr-dialog-title-${reactId}`;
  const descriptionId = descriptionIdProp ?? `tr-dialog-desc-${reactId}`;

  useEffect(() => {
    if (!open || !onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !closeDisabled) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, closeDisabled]);

  if (!open) return null;

  const labelledBy = title != null || titleIdProp ? titleId : undefined;
  const describedBy = description != null || descriptionIdProp ? descriptionId : undefined;

  const node = (
    <div
      className={['tr-dialog-scrim', scrimClassName, className].filter(Boolean).join(' ')}
      role="presentation"
      style={zIndex != null ? { zIndex } : undefined}
      onClick={() => {
        if (dismissOnScrim && onClose && !closeDisabled) onClose();
      }}
    >
      <div
        className={['tr-dialog', `tr-dialog--${size}`, panelClassName].filter(Boolean).join(' ')}
        role={role}
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        onClick={(e) => e.stopPropagation()}
      >
        {showClose && onClose ? (
          <button
            type="button"
            className="tr-dialog__close"
            aria-label={closeLabel}
            disabled={closeDisabled}
            onClick={onClose}
          >
            <X width={16} height={16} aria-hidden />
          </button>
        ) : null}

        {title != null ? (
          <h2 id={titleId} className="tr-dialog__title">
            {title}
          </h2>
        ) : null}

        {description != null ? (
          <div id={descriptionId} className="tr-dialog__desc">
            {description}
          </div>
        ) : null}

        {children != null ? <div className="tr-dialog__body">{children}</div> : null}

        {footer != null ? <div className="tr-dialog__footer">{footer}</div> : null}
      </div>
    </div>
  );

  return portal ? createPortal(node, document.body) : node;
}
